import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Container,
  Paper,
  Alert,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Cookies from "js-cookie";
import { styled } from "@mui/material/styles";
import {
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useProfile } from "../contexts/ProfileContext";

const StyledPaper = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(8),
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  borderRadius: "10px",
  boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
}));

const SocialButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "100%",
  padding: theme.spacing(1.5),
  textTransform: "none",
  fontSize: "1rem",
}));

const Login = () => {
  // useEffect(() => {
  //   localStorage.clear();
  // }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(
    location.search.includes("tab=corporate") ||
      location.search.includes("userType=corporate")
      ? "corporate"
      : "candidate",
  );

  const [error, setError] = useState("");
  const {
    resetTempProfileData,
    setProfile,
    setProfileReviewStatus,
    fetchCorporateProfileStatus,
  } = useProfile();

  const token = Cookies.get("token");
  useEffect(() => {
    if (token) {
      // Decode token to get user type
      let loggedUserType = null;
      try {
        const decoded = jwtDecode(token);
        loggedUserType =
          decoded.userType || decoded.user_type || decoded.usertype || null;
        if (loggedUserType) loggedUserType = loggedUserType.toLowerCase();
      } catch (e) {
        loggedUserType = null;
      }

      // Redirect based on user type
      if (loggedUserType === "corporate") {
        navigate("/corporate/profile");
      } else {
        navigate("/candidate");
      }
    }
  }, [token, navigate]);

  // Check for error parameter in URL (from OAuth redirects)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorParam = params.get("error");
    console.log("🔍 DEBUG: error param:", params);
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clear the error parameter from URL
      setUserType(params.get("userType"));
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const trimmedEmail = formData.username.trim();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email: trimmedEmail,
            password: formData.password,
            userType: userType,
          }),
        },
      );

      const contentType = response.headers.get("content-type");

      if (!response.ok) {
        const text = await response.text();

        try {
          const data = JSON.parse(text);

          if (typeof data === "string") {
            setError(data);
            setLoading(false);
            return;
          }

          // Show a user-friendly message for corporate under review
          if (
            data.error &&
            data.error
              .toLowerCase()
              .includes("corporate account is currently under review")
          ) {
            setError(
              "Your corporate account is under review. Please wait for approval before logging in.",
            );
            setLoading(false);
          } else {
            setError(data.error || data.message || "Login failed");
            setLoading(false);
          }
        } catch (e) {
          setError("Login failed: " + text);
          setLoading(false);
        }
        return;
      }

      const data = await response.json();

      // Clear old user's temp data BEFORE setting the new token
      // so fetchAll can seed fresh data without it being cleared afterwards
      localStorage.removeItem("tempProfileData");
      resetTempProfileData();

      Cookies.set("token", data.token, {
        expires: 7, // 20 seconds from now
        secure: true,
        sameSite: "Strict",
      });

      // Store the token
      // localStorage.setItem("token", data.token);
      // Dispatch custom event so ProfileContext updates token state
      window.dispatchEvent(new Event("token-updated"));

      // Decode token to get user type
      let loggedUserType = null;
      try {
        const decoded = jwtDecode(data.token);
        loggedUserType =
          decoded.userType || decoded.user_type || decoded.usertype || null;
        if (loggedUserType) loggedUserType = loggedUserType.toLowerCase();
      } catch (e) {
        loggedUserType = null;
      }

      // Immediately fetch profile and review status
      const fetchProfileAndStatus = async () => {
        const token = data.token;
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch profile
        const profileRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile`,
          { headers },
        );
        let profile = null;
        if (profileRes.ok) {
          profile = await profileRes.json();
        }

        // Fetch review status
        const reviewRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile/review-status`,
          { headers },
        );
        let reviewStatus = null;
        if (reviewRes.ok) {
          reviewStatus = await reviewRes.json();
        }
        console.log(
          "✅ Fetched profile and review status after login",
          reviewStatus,
        );
        // Set in context so UI updates immediately
        setProfile(profile);
        setProfileReviewStatus(reviewStatus);

        // Fetch corporate profile status if corporate user
        if (loggedUserType === "corporate") {
          await fetchCorporateProfileStatus();
        }

        setLoading(false);
        // Now, decide what to do:
        if (loggedUserType === "corporate") {
          const requiresProfile = await fetchCorporateProfileStatus();
          if (requiresProfile === false) {
            navigate("/corporate/interviews");
          } else {
            navigate("/corporate/profile");
          }
        } else {
          // For candidates, redirect to /candidate which will use the default redirect logic
          navigate("/candidate");
        }
      };
      await fetchProfileAndStatus();
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleUserTypeChange = (event, newValue) => {
    const params = new URLSearchParams(window.location.search);
    params.set("userType", newValue);
    setUserType(newValue);
    setFormData({ username: "", password: "" });
    setError("");
  };

  const handleGoogleLogin = () => {
    const googleAuthUrl = `${process.env.REACT_APP_API_URL}/api/auth/google?userType=${userType}`;
    window.location.href = googleAuthUrl;
  };

  const handleMicrosoftLogin = () => {
    const microsoftAuthUrl = `${process.env.REACT_APP_API_URL}/api/auth/microsoft?userType=${userType}`;
    window.location.href = microsoftAuthUrl;
  };

  return (
    <Container component="main" maxWidth="xs">
      <StyledPaper>
        <Box
          component="img"
          src="/bb-logo.png"
          alt="Logo"
          sx={{
            height: 70,
            width: 70,
            mb: 2,
            objectFit: "contain",
          }}
        />

        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          Sign In
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
            {error}
          </Alert>
        )}
        <Tabs value={userType} onChange={handleUserTypeChange} sx={{ mb: 2 }}>
          <Tab value="candidate" label="Candidate" />
          <Tab value="corporate" label="Corporate" />
        </Tabs>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={formData.username}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
          <Divider sx={{ my: 3 }}>OR</Divider>
          <>
            <SocialButton
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
            >
              Sign in with Google
            </SocialButton>
          </>

          <SocialButton
            variant="outlined"
            startIcon={<MicrosoftIcon />}
            onClick={handleMicrosoftLogin}
          >
            Sign in with Microsoft
          </SocialButton>
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2">
              New user?{" "}
              <Link
                to={`/register?tab=${userType}`}
                style={{ textDecoration: "none", color: "#1976d2" }}
              >
                Click here to register
              </Link>
            </Typography>
          </Box>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default Login;
